import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify } from '@mdi/js';
import ContextMenu from '../../../ContextMenu/ContextMenu';
import ContextMenuItem from '../../../ContextMenu/ContextMenuItem';

interface ISearchFieldConfig {
    onChange: (newValue: string) => void;
    placeholder?: string;
    optionsConfig?: {
        options: {
            key: string;
            label: string;
            icon?: string;
        }[];
        icon: string;
        onOptionSelect?: (option: string) => void;
    };
    loading?: boolean;
    onSubmit?: () => void;
    resetToken?: number;
    isDisabled?: boolean;
}

export default ({
    onChange,
    placeholder,
    optionsConfig,
    loading,
    onSubmit,
    resetToken,
    isDisabled = false,
}: ISearchFieldConfig) => {
    const { t } = useTranslation('translations', { keyPrefix: 'uploadDialog' });
    const [searchText, setSearchText] = useState<string>('');
    const searchRef = useRef();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const onInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(ev.target.value);
        onChange(ev.target.value);
    };

    const onClearInput = () => {
        setSearchText('');
        onChange('');
    };

    const onEnterKey = (ev: React.KeyboardEvent<HTMLDivElement>) => {
        if (ev.key === 'Enter') {
            ev.preventDefault();
            onSubmit?.();
        }
    };

    useEffect(() => {
        return () => {
            setSearchText('');
            setAnchorEl(null);
        };
    }, [resetToken]);

    return (
        <TextField
            value={searchText}
            fullWidth
            onKeyDown={onEnterKey}
            onChange={onInputChange}
            placeholder={placeholder ?? t('search')}
            disabled={isDisabled}
            sx={{ input: { py: 1 } }}
            slotProps={{
                input: {
                    startAdornment: loading && (
                        <InputAdornment position='start'>
                            <CircularProgress size='24px' />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <Stack direction='row' sx={{ alignItems: 'center' }}>
                            <InputAdornment position='end'>
                                {searchText.length > 0 && (
                                    <IconButton onClick={onClearInput}>
                                        <Icon path={mdiClose} size={1} />
                                    </IconButton>
                                )}
                                {onSubmit ? (
                                    <IconButton onClick={onSubmit} disabled={isDisabled}>
                                        <Icon path={mdiMagnify} size={1} />
                                    </IconButton>
                                ) : (
                                    <Icon path={mdiMagnify} size={1} />
                                )}
                            </InputAdornment>
                            {optionsConfig && (
                                <>
                                    <IconButton
                                        ref={searchRef}
                                        onClick={() => setAnchorEl(searchRef.current)}
                                        sx={{ p: '5px' }}
                                        disabled={isDisabled}
                                    >
                                        <Icon path={optionsConfig.icon} size={1.0} />
                                    </IconButton>
                                    <ContextMenu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={() => setAnchorEl(null)}
                                    >
                                        {optionsConfig.options.map(x => (
                                            <ContextMenuItem
                                                key={x.key}
                                                text={x.label}
                                                icon={x.icon || undefined}
                                                onClick={() => {
                                                    setAnchorEl(null);
                                                    optionsConfig.onOptionSelect?.(x.key);
                                                }}
                                            />
                                        ))}
                                    </ContextMenu>
                                </>
                            )}
                        </Stack>
                    ),
                },
            }}
        />
    );
};
